/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form } from 'react-bootstrap';
import {
  useForm,
  Controller,
  FieldValues,
  SubmitHandler
} from 'react-hook-form';
import { useQuery } from 'react-query';
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import Flatpickr from 'react-flatpickr';
import { UilCalendarAlt } from '@iconscout/react-unicons';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import { UzbekLatin } from 'flatpickr/dist/l10n/uz_latn.js';
import { useSearchParams } from 'react-router-dom';

import Button from 'components/base/Button';
import warehouseService from 'service/warehouse';
import usersService from 'service/users';

interface CreateFilterInfoProps {
  savedValues?: any;
  setSavedValues?: any;
  setOpenModal: (show: boolean) => void;
  setGetFilterData?: any;
  dateFrom?: any;
  dateTo?: any;
  setDateFrom?: any;
  setDateTo?: any;
}

const CreateFilterInfo = ({
  savedValues,
  setSavedValues,
  setOpenModal,
  setGetFilterData,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo
}: CreateFilterInfoProps) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { control, handleSubmit, reset, setValue, watch, register } = useForm();

  const [warehouseOption, setWarehouseOption] = useState<any>([]);
  const [usersOption, setUsersOption] = useState<any>([]);

  const [loading, setLoading] = useState(false);

  const handleStartDateChange = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      const startDate = new Date(date[0]);
      startDate.setHours(0, 0, 0, 0); // Set time to 00:00
      const startDateInMillis = startDate.getTime();
      setDateFrom(startDateInMillis);
    } else if (!Array.isArray(date)) {
      const startDate = new Date(date);
      startDate.setHours(0, 0, 0, 0); // Set time to 00:00
      const startDateInMillis = startDate.getTime();
      setDateFrom(startDateInMillis);
    } else {
      setDateFrom?.(null);
    }
  };

  const handleEndDateChange = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      const endDateInMillis = date[0]?.getTime();
      setDateTo(endDateInMillis);
    } else if (!Array.isArray(date)) {
      const endDateInMillis = date.getTime();
      setDateTo(endDateInMillis);
    } else {
      setDateTo?.(null);
    }
  };

  //GET Warehouse to options
  useQuery(['GET_WAREHOUSE'], async () => {
    await warehouseService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.ware_houses?.map((option: any) => ({
          value: option.id,
          label: option.name
        }));
        setWarehouseOption(options);
      });
  });

  //GET Users to options
  useQuery(['GET_USERS'], async () => {
    await usersService.getList({ offset: 0, limit: 100 }).then((res: any) => {
      const options = res?.data?.users?.map((option: any) => ({
        value: option.id,
        label: `${option.first_name}  ${option?.last_name}`
      }));
      setUsersOption(options);
    });
  });

  //Get by id
  const fetchData = () => {
    if (!savedValues || savedValues.length === 0) {
      reset({
        inventory_number: '',
        stock: '',
        responsible: ''
      });
      setDateFrom?.(null);
      setDateTo?.(null);
      return;
    }

    savedValues?.map((el: any) => {
      const computed = {
        inventory_number: el?.inventory_number,
        responsible: el?.user_id,
        stock: el?.warehouse_id
      };
      reset(computed);
      setDateFrom?.(el?.date_from ? +el.date_from : null);
      setDateTo?.(el?.date_to ? +el.date_to : null);
    });
  };

  const getFlatpickrLocale = () => {
    switch (i18n.language) {
      case 'ru':
        return Russian;
      case 'uz':
        return UzbekLatin;
      default:
        return undefined;
    }
  };

  const updateSearchParams = (params: Record<string, string | undefined>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const onSubmit: SubmitHandler<FieldValues> = data => {
    setLoading(true);

    /* prettier-ignore */
    const adjustedDateTo =
      !dateFrom && !dateTo
        ? undefined
        : dateTo === dateFrom
          ? dateFrom + 86400000
          : dateTo
            ? dateTo + 86400000
            : undefined;

    const createFilterData = {
      // prettier-ignore
      date_from: dateFrom ? dateFrom : undefined,
      date_to: adjustedDateTo,
      inventory_number: data?.inventory_number
        ? data?.inventory_number
        : undefined,
      user_id: data?.responsible ? data?.responsible : undefined,
      warehouse_id: data?.stock ? data?.stock : undefined
    };

    if (createFilterData) {
      setGetFilterData(createFilterData);
      setSavedValues([
        {
          date_from: dateFrom ? dateFrom : undefined,
          date_to: dateTo ? dateTo : undefined,
          inventory_number: data?.inventory_number
            ? data?.inventory_number
            : undefined,
          user_id: data?.responsible ? data?.responsible : undefined,
          warehouse_id: data?.stock ? data?.stock : undefined
        }
      ]);
      setOpenModal(false);
      setLoading(false);
      updateSearchParams({
        dateFrom: dateFrom?.toString() || '',
        dateTo: adjustedDateTo?.toString() || '',
        inventoryNumber: data?.inventory_number || '',
        stock: data?.stock || '',
        responsible: data?.responsible || ''
      });
    }
  };

  const handleClear = () => {
    reset({
      inventory_number: '',
      user_id: '',
      stock: ''
    });
    setGetFilterData({});
    setDateFrom(null);
    setDateTo(null);
    setSavedValues([]);
    setOpenModal(false);
    updateSearchParams({
      dateFrom: '',
      dateTo: '',
      inventoryNumber: '',
      stock: '',
      responsible: ''
    });
  };

  useEffect(() => {
    if (savedValues?.length > 0) {
      fetchData();
    }
  }, [savedValues]);
  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: '10px'
      }}
    >
      <Form.Floating className="md-4 w-100">
        <Form.Control
          {...register('inventory_number')}
          type="number"
          placeholder={t('inventory_number')}
          onFocus={e => e.target.select()}
        />
        <label htmlFor="floatingInputCustom">{t('inventory_number')}</label>
      </Form.Floating>

      <Form.Group className="md-4 w-100">
        <div className="react-select-container ">
          <Controller
            name="stock"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Form.Floating>
                <Form.Select {...field}>
                  <option className="d-none" value=""></option>
                  {warehouseOption?.map((option: any) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>

                <label htmlFor="floatingInputCustom">{t('warehouse')}</label>
                {watch('stock') !== '' && (
                  <Button
                    variant="link"
                    style={{
                      position: 'absolute',
                      right: '5%',
                      top: '15%',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setValue('stock', '');
                    }}
                  >
                    <FeatherIcon icon="delete" size={16} />
                  </Button>
                )}
              </Form.Floating>
            )}
          />
        </div>
      </Form.Group>

      <Form.Group>
        <div className="react-select-container position-relative">
          <Controller
            name="responsible"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Form.Floating className="users">
                <Form.Select {...field}>
                  <option className="d-none" value=""></option>
                  {usersOption?.map((option: any) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <label htmlFor="floatingInputCustom">
                  {t('financially_responsible_person')}
                </label>
                {watch('responsible') !== '' && (
                  <Button
                    variant="link"
                    style={{
                      position: 'absolute',
                      right: '5%',
                      top: '15%',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setValue('responsible', '');
                    }}
                  >
                    <FeatherIcon icon="delete" size={16} />
                  </Button>
                )}
              </Form.Floating>
            )}
          />
        </div>
      </Form.Group>

      <Form.Group className="flatpickr-input-container">
        <Form.Floating>
          <Flatpickr
            className={classNames('form-control')}
            options={{
              nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
              prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
              locale: getFlatpickrLocale(),
              monthSelectorType: 'static',
              onDayCreate: (...[, , , dayElem]) => {
                if (
                  dayElem.dateObj.getDay() === 5 ||
                  dayElem.dateObj.getDay() === 6
                ) {
                  dayElem.className += ' weekend-days';
                }
              },
              dateFormat: 'M j, Y',
              disableMobile: true
            }}
            value={dateFrom}
            onChange={handleStartDateChange}
            placeholder={t('date_from')}
          />
          <label htmlFor="startDatepicker" className="ps-6">
            {t('date_from')}
          </label>
        </Form.Floating>
        <UilCalendarAlt className="flatpickr-icon text-700" size={16} />
      </Form.Group>

      <Form.Group className="flatpickr-input-container">
        <Form.Floating>
          <Flatpickr
            className={classNames('form-control')}
            options={{
              nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
              prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
              locale: getFlatpickrLocale(),
              monthSelectorType: 'static',
              onDayCreate: (...[, , , dayElem]) => {
                if (
                  dayElem.dateObj.getDay() === 5 ||
                  dayElem.dateObj.getDay() === 6
                ) {
                  dayElem.className += ' weekend-days';
                }
              },
              dateFormat: 'M j, Y',
              disableMobile: true
            }}
            value={dateTo}
            onChange={handleEndDateChange}
            placeholder={t('date_to')}
          />
          <label htmlFor="startDatepicker" className="ps-6">
            {t('date_to')}
          </label>
        </Form.Floating>
        <UilCalendarAlt className="flatpickr-icon text-700" size={16} />
      </Form.Group>

      <Form.Group style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
        <Button
          style={{ height: '48px', width: '100%' }}
          variant="outline-primary"
          type="button"
          onClick={() => handleClear()}
        >
          {t('reset')}
        </Button>
        <Button
          style={{ height: '48px', width: '100%' }}
          variant="primary"
          type="submit"
          loading={loading}
          loadingPosition="start"
        >
          {t('apply')}
        </Button>
      </Form.Group>
    </Form>
  );
};

export default CreateFilterInfo;
